import { Ingredient, Product } from "src/api/client-api/products/useGetOneProduct";
import { CompletionMetadata, Completions, CompletionService } from "../../CompletionService";
import { IngredientReferenceUnified, buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { IngredientPostListQuery } from "src/api/client-api/post/useGetIngredientsPosts";

type EntityId = number;
export type IngredientsCompletionsMetadata = Record<EntityId, CompletionMetadata>;

type ConstructorOptions = {
    product: Product | undefined;
    ingredientPosts: IngredientPostListQuery[];
    ingredientReferences: IngredientReferenceUnified[] | undefined;
    scoreId: number | undefined;
};
export class ProductIngredientCompletionService extends CompletionService {
    private ingredientsMetadata: IngredientsCompletionsMetadata = {};
    private readonly ingredientReferences: IngredientReferenceUnified[];
    private readonly scoreId: number | undefined;
    private readonly ingredients: Ingredient[];
    private readonly ingredientPosts: IngredientPostListQuery[];

    public constructor(options: ConstructorOptions) {
        super();
        const { product, ingredientPosts, ingredientReferences, scoreId } = options;

        this.ingredientReferences = ingredientReferences ?? [];
        this.scoreId = scoreId;
        this.ingredientPosts = ingredientPosts;

        const ingredients =
            product?.productIngredients
                ?.filter((pIng) => pIng.ingredient !== undefined)
                .map((pIng) => pIng.ingredient!) ?? [];
        this.ingredients = ingredients;

        this.computeIngredientsMetadata();
    }

    private computeIngredientsMetadata() {
        const initialAcc: IngredientsCompletionsMetadata = {};
        const metadata = this.ingredients.reduce((acc, ingredient) => {
            const ingredientSuppliersIds =
                ingredient.suppliers?.map((ingredientSupplier) => ingredientSupplier.supplier.id) ?? [];
            const ingredientPosts = this.ingredientPosts.find((i) => i?.ingredientId === ingredient.id)?.posts ?? [];
            const ingredientSuppliersPosts = ingredientPosts.filter(
                (post) => post.organization && ingredientSuppliersIds.includes(post.organization.id),
            );

            const ingredientRefeferenceId = ingredient.ingredientReferenceId;
            // Match by composite id so we don't collide ids between sure / foodpilot sources.
            const ingredientCompositeId =
                ingredientRefeferenceId !== undefined ?
                    buildCompositeId(ingredient.referenceSource ?? "sure", ingredientRefeferenceId)
                :   undefined;
            const ingredientReference = this.ingredientReferences?.find(
                (ref) => ref.compositeId === ingredientCompositeId,
            );
            // Only sure references carry an SSQ — the unified DTO exposes it as `ssqId`.
            const ingredientReferenceHasSsq = !!ingredientReference?.ssqId;
            const ingredientHasSuppliers = !!ingredient.suppliers?.length;
            const hasProgress =
                ingredientHasSuppliers && ingredientReferenceHasSsq && ingredientSuppliersPosts.length > 0;
            const isValid = ingredientReferenceHasSsq || ingredientSuppliersPosts.length > 0;
            const scoreRef = ingredientReference?.scores?.find((score) => score.scoreId === this.scoreId);

            const ingredientMetadata: CompletionMetadata = {
                isValid,
                hasProgress,
                reference: {
                    score: scoreRef?.value ?? undefined,
                    label: ingredientReference?.name,
                },
                posts: ingredientSuppliersPosts,
            };
            acc[ingredient.id] = ingredientMetadata;
            return acc;
        }, initialAcc);

        this.ingredientsMetadata = metadata;
    }

    public getMetadata(): IngredientsCompletionsMetadata {
        return this.ingredientsMetadata;
    }

    public getCompletions(): Completions {
        const emptyCompletion = this.getEmptyCompletions();

        let grandTotalAverage = 0;
        const ingredientCompletions = this.ingredients.reduce((acc, ingredient) => {
            const metadata = this.ingredientsMetadata[ingredient.id];
            const completion = this.getPostListCompletion(metadata.posts);

            acc.byEntityId[ingredient.id] = completion;

            acc.totalAverageItems += completion.totalAverageItems;
            acc.completedCount += completion.completedCount;
            acc.count += completion.count;
            grandTotalAverage += completion.weightedAverage * completion.totalAverageItems;

            return acc;
        }, emptyCompletion);

        if (ingredientCompletions.totalAverageItems > 0) {
            ingredientCompletions.weightedAverage = grandTotalAverage / ingredientCompletions.totalAverageItems;
        }

        return ingredientCompletions;
    }
}
